import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerValueCvd001Api, type Cvd001Status, type SprintDashboard, type SprintValidation } from '../../api/customer-value-cvd001';

type Tab = 'overview' | 'modules' | 'metrics' | 'providers' | 'jobs' | 'gates' | 'status';

export function CustomerValueCvd001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [metricTypes, setMetricTypes] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Cvd001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-emerald-200 bg-white dark:border-emerald-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-emerald-700 dark:text-emerald-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, metrics, prov, jb, gt, st] = await Promise.all([
        customerValueCvd001Api.dashboard(), customerValueCvd001Api.valueModules(),
        customerValueCvd001Api.metricTypes(), customerValueCvd001Api.providers(),
        customerValueCvd001Api.backgroundJobs(), customerValueCvd001Api.qualityGates(),
        customerValueCvd001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setMetricTypes(metrics); setProviders(prov);
      setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await customerValueCvd001Api.validate({
        every_value_traceable: true, every_metric_explainable: true,
        roi_operational: true, executive_reports_operational: true,
        benchmarks_operational: true, performance_budgets_met: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Customer Value Dashboard passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Value Modules' },
    { id: 'metrics', label: 'Metric Types' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-emerald-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>CVD-000001 · P0 · Mandatory Before Public Launch</p>
        <h1 className="text-2xl font-semibold">Customer Value Dashboard</h1>
        <p className={`text-sm ${muted}`}>ROI engine, business impact, renewal justification — transform subscription renewals from opinion into measurable evidence.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/engineering-excellence-exl001" className={`${accent} hover:underline`}>EXL-000001</Link>
          <span className={muted}>→</span>
          <Link to="/customer-value" className={`${accent} hover:underline`}>Live Customer Value Dashboard</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-emerald-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
        <button type="button" onClick={validate} disabled={busy} className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm text-white disabled:opacity-50">Validate Sprint</button>
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Objectives', value: dashboard.objectives },
            { label: 'Value Modules', value: dashboard.value_modules },
            { label: 'Metric Types', value: dashboard.value_metric_types },
            { label: 'Quality Gates', value: dashboard.quality_gates },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'modules' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {modules.map((m) => (
            <div key={m.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{m.name}</p>
              <p className={`text-xs ${muted}`}>{m.category} · {m.key}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'metrics' && (
        <div className="grid gap-2 sm:grid-cols-3">
          {metricTypes.map((m) => (
            <div key={m.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{m.name}</p>
              <p className={`text-xs ${muted}`}>{m.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{p.name}</p>
              <p className={`text-xs font-mono ${muted}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {jobs.map((j) => (
            <div key={j.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{j.name}</p>
              <p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'gates' && (
        <div className="space-y-2">
          {gates.map((g) => (
            <div key={g.key} className={`flex items-center justify-between rounded-lg border p-3 ${card}`}>
              <span>{g.name}</span>
              {g.blocking && <span className="text-xs text-red-600">blocking</span>}
            </div>
          ))}
          {validation && (
            <p className={`text-sm ${validation.valid ? 'text-emerald-600' : 'text-red-600'}`}>
              Validation: {validation.valid ? 'PASS' : `FAIL — ${validation.gaps?.join(', ')}`}
            </p>
          )}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <pre className="overflow-x-auto text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
